import { useEffect } from "react";
import { ResponsiveImage, Subtitle1, YStack } from "@medbillai/ui";
import { OnboardingScreen } from "../../components/OnboardingScreen";
import { type OnDone, tagged } from "../../lib/flow-engine";
import { onboardingStorage } from "../../lib/storage/instances";
import type { Intent } from "./types";
import { isIntent } from "./types";

// Shared logo asset with the auth stage screen so the G symbol stays the same
// size across stages (the frame reserves space for the success check).
const SUCCESS_LOGO_SRC = {
  web: "/static/success-logo.png",
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-require-imports
  native: require("../../assets/success-logo.png")
};
export type AccountCreatedOutcomes = {
  ready: {
    intent: Intent | undefined;
  };
};
const Outcome = tagged<AccountCreatedOutcomes>();
const readStashedIntent = async (): Promise<Intent | undefined> => {
  const raw = await onboardingStorage.get("intent");
  if (raw === null || !isIntent(raw)) {
    return undefined;
  }
  return raw;
};
const MIN_DISPLAY_MS = 1000;
export function AccountCreated({
  onDone
}: {
  onDone: OnDone<AccountCreatedOutcomes>;
}) {
  useEffect(() => {
    let cancelled = false;

    // Promise.all timeout is for two things: Showing a intermittent "account
    // created" message to the user (for 1 second), and loading the intent from
    // the stashed storage to populate the next screen.
    void Promise.all([readStashedIntent().catch(() => undefined), new Promise<void>(resolve => setTimeout(resolve, MIN_DISPLAY_MS))]).then(([intent]) => {
      if (cancelled) {
        return;
      }
      onDone(Outcome.ready({
        intent
      }));
    });
    return () => {
      cancelled = true;
    };
  }, [onDone]);
  return <OnboardingScreen showBackgroundGradient data-sentry-element="OnboardingScreen" data-sentry-component="AccountCreated" data-sentry-source-file="AccountCreated.tsx">
      <OnboardingScreen.Nav showLogo={false} data-sentry-element="OnboardingScreen.Nav" data-sentry-source-file="AccountCreated.tsx" />
      <OnboardingScreen.Content data-sentry-element="OnboardingScreen.Content" data-sentry-source-file="AccountCreated.tsx">
        <YStack flex={1} alignItems="center" justifyContent="center" gap="$sp1.5" data-sentry-element="YStack" data-sentry-source-file="AccountCreated.tsx">
          <ResponsiveImage src={SUCCESS_LOGO_SRC} alt="Account created" objectFit="contain" width={56} height={56} data-sentry-element="ResponsiveImage" data-sentry-source-file="AccountCreated.tsx" />
          <Subtitle1 textAlign="center" data-sentry-element="Subtitle1" data-sentry-source-file="AccountCreated.tsx">Account created!</Subtitle1>
        </YStack>
      </OnboardingScreen.Content>
    </OnboardingScreen>;
}